import { useUser } from '@myosh/myosh-login';
import type { CheckboxChangeEventType } from '@myosh/odin-components';
import { Checkbox, Dialog, Input } from '@myosh/odin-components';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Notifications from '../../../components/common/notifications.component';
import { getDashboardFiltersCombination } from '../../../helpers/dashboard-filter-formatter';
import type { DateFilterRangeType } from '../../../redux/config/filters-api';
import { useGetUserAppliedFiltersQuery } from '../../../redux/config/filters-api';
import type { PostViewExtended, PutPatchViewInterface } from '../../../redux/config/views';
import { useCreateViewMutation, useGetAllViewsQuery, useUpdateViewMutation } from '../../../redux/config/views';
import { useAppDispatch } from '../../../redux/hooks';
import { setSelectedView } from '../../../redux/slices/selected-view';
import { useSelectedView } from '../../Charts/hooks/use-selected-view';
import type { ViewNameOption } from './view-dropdown';
import type { ManageViewsModalProps } from './view-handler.component';

const dialogId = 'view-save-modal-dialog';
const toasterId = 'view-save-modal-toaster';
const TOAST_DURATION = 3000;

export const ViewSaveModal = ({ visible, onClose, viewSaved }: ManageViewsModalProps) => {
  const [viewsOptions, setViewOptions] = useState<Array<ViewNameOption>>([]);
  const [isGlobalView, setIsGlobalView] = useState<boolean>(false);
  const [viewName, setViewName] = useState<string>('');

  const { data, isFetching } = useGetAllViewsQuery();
  const navigateToView = useRef<boolean>(true);
  const [addView] = useCreateViewMutation();
  const [updateView] = useUpdateViewMutation();
  const dispatch = useAppDispatch();
  const { data: userAppliedFilters, isFetching: isFetchingUserAppliedFilters } = useGetUserAppliedFiltersQuery();
  const user = useUser();
  const applicationView = useSelectedView();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (applicationView && visible) {
      setIsGlobalView(!applicationView.privateView);
      setViewName(applicationView.name || '');
    }
  }, [applicationView, visible]);

  useEffect(() => {
    if (data && !isFetching) {
      const viewOptions = data.map((view) => {
        return {
          text: view.name,
          value: view.id,
          defaultView: view.defaultView,
          ...view,
        };
      });
      setViewOptions(viewOptions);
    }
  }, [data, isFetching]);

  const updateViewHandler = useCallback(
    (updatedView: PutPatchViewInterface) => {
      const loadingToast = toast.loading(t('performingSave'), { toasterId });
      setIsSaving(true);
      updateView(updatedView)
        .unwrap()
        .then((result) => {
          toast.dismiss(loadingToast);
          if (result) {
            toast.success(t('updateViewSuccess'), { duration: TOAST_DURATION });
            onClose();
            setIsSaving(false);
          } else {
            toast.error(t('updateViewFailure'), { duration: TOAST_DURATION, toasterId });
            setIsSaving(false);
          }
        })
        .catch(() => {
          toast.dismiss(loadingToast);
          toast.error(t('updateViewFailure'), { duration: TOAST_DURATION, toasterId });
          setIsSaving(false);
        });
    },
    [dispatch, updateView]
  );

  const addViewHandler = useCallback(
    (newView: PostViewExtended) => {
      navigateToView.current = true;
      setIsSaving(true);
      const loadingToast = toast.loading(t('performingSave'), { toasterId });
      return addView(newView)
        .unwrap()
        .then((result) => {
          toast.dismiss(loadingToast);
          if (result) {
            dispatch(setSelectedView({ id: result }));
            toast.success(t('viewCreated'), { duration: TOAST_DURATION });
            viewSaved?.();
            onClose();
            setIsSaving(false);
          }
        })
        .catch(() => {
          toast.dismiss(loadingToast);
          setIsSaving(false);
        });
    },
    [addView, dispatch]
  );
  /**
   * This method takes care of updating an existing view or creating a new one.
   * - Clicking save when view name is not updated and no changes done --> nothing happens
   * - Clicking save with the same name, updates the view
   * - Clicking save with a new name provided
   */
  const saveChangesCallback = () => {
    if (viewName) {
      if (viewName === 'Default') {
        toast.error(t('defaultErrorUpdate'));
        return;
      }

      if (
        viewName.trim() === applicationView?.name?.trim() &&
        isEqual(userAppliedFilters?.dashboardFiltersCombination, applicationView?.dashboardFiltersCombination) &&
        (applicationView.privateView ?? false) === !isGlobalView
      ) {
        onClose();
        return;
      }
      const existingView = viewsOptions.find((viewOption) => viewOption?.name?.trim() === viewName.trim());
      const payload: PostViewExtended = {
        name: viewName.trim(),
        dashboardFiltersCombination: getDashboardFiltersCombination({
          dateFilterRangeType: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView
            ?.dateFilterRangeType as DateFilterRangeType,
          fromDate: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.fromDate,
          toDate: userAppliedFilters?.dashboardFiltersCombination?.dateFiltersForView?.toDate,
          hierarchyValues: userAppliedFilters?.dashboardFiltersCombination?.hierarchyFilters?.hierarchyValues,
          showArchived: userAppliedFilters?.dashboardFiltersCombination?.hierarchyFilters?.showArchived,
        }),
      };

      payload.privateView = !isGlobalView;
      if (existingView) {
        navigateToView.current = false;
        updateViewHandler({ id: existingView.id, ...payload });
      } else {
        payload.userId = user.state.user?.preferred_username ?? '';
        payload.privateView = !isGlobalView;
        payload.defaultView = false;
        addViewHandler(payload);
      }
    } else {
      toast.error(t('nameRequired'), { duration: TOAST_DURATION });
    }
  };

  const handleViewNameChange = (value?: string) => {
    setViewName(value || '');
  };

  const handleIsGlobalChange = (value?: CheckboxChangeEventType) => {
    setIsGlobalView(!!value?.checked);
  };

  return (
    <>
      <Dialog
        header={t('saveCurrentView')}
        visible={visible}
        onClose={onClose}
        buttons={[
          {
            type: 'primary',
            name: 'save',
            text: t('save'),
            onClick: saveChangesCallback,
            disabled: isFetchingUserAppliedFilters || isSaving,
          },
        ]}
        className="min-w-[350px]"
        buttonType="submit"
        closeOnClickOutside={false}
        id={dialogId}
      >
        <div className="my-2 flex flex-col gap-4">
          <Input
            name="viewName"
            label="Name"
            inputStyles="rounded-sm py-2"
            value={viewName}
            onChange={handleViewNameChange}
            disabled={isFetchingUserAppliedFilters || isSaving}
          />
          <Checkbox
            key={String(isGlobalView)}
            name="globalView"
            label={t('globalView')}
            className="rounded-sm"
            initialChecked={isGlobalView}
            value={String(isGlobalView)}
            onChange={handleIsGlobalChange}
            disabled={isFetchingUserAppliedFilters || isSaving}
          />
        </div>
      </Dialog>
      <Notifications appendTo={document.getElementById(dialogId) ?? document.body} toasterId={toasterId} />
    </>
  );
};
